import SmartyStreetsSDK, { usStreet } from "smartystreets-javascript-sdk";
import sdkUtils from "smartystreets-javascript-sdk-utils";
import Settings from "../settings";

const SmartyStreetsCore = SmartyStreetsSDK.core;
const creds = new SmartyStreetsCore.SharedCredentials(Settings.smartyStreetsKey || "");
const usStreetClientBuilder = new SmartyStreetsCore.ClientBuilder(creds);
const streetClient = usStreetClientBuilder.buildUsStreetApiClient();

export type NullableAddress = {
  address1: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}

type Address = {
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
}

export type ValidationResult = {
  wasFound: boolean;
  isValid: boolean;
  isAmbiguous: boolean;
  isMissingSecondary: boolean;
  suggestedAddress: Address;
}

export const getAddress = (data: NullableAddress): Address => ({
  address1: data.address1 || "",
  address2: data.address2 || "",
  city: data.city || "",
  state: data.state || "",
  zip: data.zip || "",
});

/** Verify a US street address and get a suggessted format for best mailing */
export const validateStreetAddress = (address: Address): Promise<ValidationResult> =>
  new Promise((resolve, reject) => {
  // http req will fail without this field
    if (!address.address1) {
      reject(new Error("Street address not found"));
      return;
    }
    const lookup = new SmartyStreetsSDK.usStreet.Lookup();
    lookup.street = address.address1;
    lookup.street2 = address.address2;
    lookup.city = address.city;
    lookup.state = address.state;
    lookup.zipCode = address.zip;
    streetClient
      .send(lookup)
      .then(({ lookups: [firstCandidate] }: { lookups: usStreet.Lookup[] }) => {
        const { result: [lookupResult] } = firstCandidate;
        if (lookupResult) {
          const { deliveryLine1, deliveryLine2, components } = lookupResult;
          resolve({
            wasFound: true,
            isValid: sdkUtils.isValid(lookup),
            isAmbiguous: sdkUtils.isAmbiguous(lookup),
            isMissingSecondary: sdkUtils.isMissingSecondary(lookup),
            suggestedAddress: {
              address1: deliveryLine1,
              address2: deliveryLine2 || "",
              city: components.cityName,
              state: components.state,
              zip: `${components.zipCode}-${components.plus4Code}`,
            },
          });
        } else {
          resolve({
            wasFound: false,
            isValid: false,
            isAmbiguous: false,
            isMissingSecondary: false,
            suggestedAddress: address,
          });
        }
      })
      .catch((err: Error | { error: Error }) => {
        reject(err);
        throw err;
      });
  });
